import { db, functions } from '../firebase';
import { collection, doc, setDoc, getDoc, getDocs, updateDoc, query, orderBy, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export async function fetchAllPlayers() {
  const snap = await getDocs(collection(db, "players"));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function getUserData(uid) {
  const userRef = doc(db, "users", uid);
  const userSnap = await getDoc(userRef);
  if (userSnap.exists()) {
    return userSnap.data();
  }
  return null;
}

export async function createUserData(uid, email) {
  const userRef = doc(db, "users", uid);
  const userData = {
    email,
    claimedPlayer: null,
    createdAt: Date.now()
  };
  await setDoc(userRef, userData);
  return userData;
}

export async function updateUserClaimedPlayer(uid, playerId) {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    claimedPlayer: playerId
  });
}

export async function updateUserIgn(uid, ign) {
  const userRef = doc(db, "users", uid);
  await updateDoc(userRef, {
    ign: ign
  });
}

export async function getAllClaimedPlayers() {
  const usersRef = collection(db, "users");
  const usersSnap = await getDocs(usersRef);
  const claimedPlayers = [];
  usersSnap.forEach(doc => {
    const data = doc.data();
    if (data.claimedPlayer) {
      claimedPlayers.push(data.claimedPlayer);
    }
  });
  return claimedPlayers;
}

export async function savePlayer(name) {
  const fn = httpsCallable(functions, 'adminSavePlayer');
  const result = await fn({ name });
  return result.data.added;
}

export async function deletePlayer(playerId) {
  const fn = httpsCallable(functions, 'adminDeletePlayer');
  await fn({ playerId });
}

export async function fetchAllGames() {
  const snap = await getDocs(query(collection(db, "games"), orderBy("timestamp", "desc")));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function deleteGame(gameId) {
  const fn = httpsCallable(functions, 'adminDeleteGame');
  await fn({ gameId });
}

// ─── Season Data Functions ─────────────────────────────────────────────────

/** Fetch season config (current season, season list, etc.) */
export async function fetchSeasonConfig() {
  const snap = await getDoc(doc(db, "config", "season"));
  if (snap.exists()) {
    return snap.data();
  }
  // Default: Season 1 if config doesn't exist yet
  return { currentSeason: 1, seasonStartedAt: 0, transitioning: false, seasons: { '1': { startedAt: 0, endedAt: null, label: 'Season 1' } } };
}

/** Fetch games filtered by season. Pass null for all seasons (all-time). */
export async function fetchGamesBySeason(season = null) {
  if (season === null) {
    return fetchAllGames();
  }
  const snap = await getDocs(
    query(collection(db, "games"), where("season", "==", season), orderBy("timestamp", "desc"))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Fetch all season snapshots for a given season (for past-season leaderboards). */
export async function fetchSeasonSnapshots(season) {
  const snap = await getDocs(
    query(collection(db, "seasonSnapshots"), where("season", "==", season))
  );
  return snap.docs.map((d) => d.data());
}

/** Fetch all season snapshots for a specific player (for all-time ELO calculation). */
export async function fetchPlayerSeasonSnapshots(playerId) {
  const snap = await getDocs(
    query(collection(db, "seasonSnapshots"), where("playerId", "==", playerId))
  );
  return snap.docs.map((d) => d.data());
}

/** Fetch casino stats snapshot for a specific user and season. */
export async function fetchCasinoSeasonSnapshot(userId, season) {
  const snap = await getDoc(doc(db, "seasonCasinoSnapshots", `${userId}_s${season}`));
  return snap.exists() ? snap.data() : null;
}

/** Fetch unboxing stats snapshot for a specific user and season. */
export async function fetchUnboxingSeasonSnapshot(userId, season) {
  const snap = await getDoc(doc(db, "seasonUnboxingSnapshots", `${userId}_s${season}`));
  return snap.exists() ? snap.data() : null;
}

/** Admin: migrate existing data to seasons framework. */
export async function adminMigrateToSeasons() {
  const fn = httpsCallable(functions, 'adminMigrateToSeasons');
  const result = await fn();
  return result.data;
}

/** Admin: start a new season. */
export async function adminStartNewSeason() {
  const fn = httpsCallable(functions, 'adminStartNewSeason');
  const result = await fn();
  return result.data;
}

// ─── Season 2: Pass & Challenge Functions ───────────────────────────────────

export async function purchasePass(passType) {
  const fn = httpsCallable(functions, 'purchasePass');
  const result = await fn({ passType });
  return result.data;
}

export async function claimPassReward(passType, tier) {
  const fn = httpsCallable(functions, 'claimPassReward');
  const result = await fn({ passType, tier });
  return result.data;
}

export async function getActiveChallenges() {
  const fn = httpsCallable(functions, 'getActiveChallenges');
  const result = await fn();
  return result.data;
}

export async function claimChallengeReward(challengeId, challengeType) {
  const fn = httpsCallable(functions, 'claimChallengeReward');
  const result = await fn({ challengeId, challengeType });
  return result.data;
}

export async function adminLaunchSeason2Features() {
  const fn = httpsCallable(functions, 'adminLaunchSeason2Features');
  const result = await fn();
  return result.data;
}

export async function adminGrantTestXp(amount) {
  const fn = httpsCallable(functions, 'adminGrantTestXp');
  const result = await fn({ amount });
  return result.data;
}

export async function adminResetPassProgress() {
  const fn = httpsCallable(functions, 'adminResetPassProgress');
  const result = await fn();
  return result.data;
}

export async function saveShowcaseItems(items) {
  const fn = httpsCallable(functions, 'saveShowcaseItems');
  const result = await fn({ items });
  return result.data;
}

export async function adminPauseSeason2Features() {
  const fn = httpsCallable(functions, 'adminPauseSeason2Features');
  const result = await fn();
  return result.data;
}
